/**
 * How long a rider has waited for Support, and how the console flags it.
 * Pure, so it is unit-tested on its own.
 */

import { Conversation } from '../../hooks/useChat';
import { waitingSince } from '../../hooks/chat/queueOrder';
import { formatAgo } from './format';

/** Amber from here: the rider has waited this many minutes for an answer. */
export const SLA_WARNING_MINUTES = 5;
/** Red from here. */
export const SLA_BREACH_MINUTES = 15;
/** The queue re-reads the clock this often, so the times keep up. */
export const SLA_TICK_MS = 30000;

export type SlaLevel = 'ok' | 'warning' | 'breach';

type SlaFields = Pick<
  Conversation,
  'status' | 'last_message' | 'handled_by' | 'waiting_since'
>;

/** When the rider started waiting: defined with the queue's order, re-exported here. */
export { waitingSince };

/** Whole minutes since `since`; 0 for a bad or future time. */
export function waitedMinutes(since: string, nowMs: number): number {
  const then = Date.parse(since);
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((nowMs - then) / 60000));
}

export function slaLevel(minutes: number): SlaLevel {
  if (minutes >= SLA_BREACH_MINUTES) return 'breach';
  if (minutes >= SLA_WARNING_MINUTES) return 'warning';
  return 'ok';
}

export const SLA_CHIP_COLOR: Record<SlaLevel, 'default' | 'warning' | 'error'> = {
  ok: 'default',
  warning: 'warning',
  breach: 'error',
};

/** "Waiting" under a minute, then "Waiting 7m", "Waiting 2h". */
export function waitingLabel(since: string, nowMs: number): string {
  return waitedMinutes(since, nowMs) < 1
    ? 'Waiting'
    : `Waiting ${formatAgo(since, nowMs)}`;
}

/** The queue row's wait, or null when the rider is not waiting. */
export function queueWait(
  c: SlaFields,
  nowMs: number
): { since: string; minutes: number; level: SlaLevel; label: string } | null {
  const since = waitingSince(c);
  if (!since) return null;
  const minutes = waitedMinutes(since, nowMs);
  return { since, minutes, level: slaLevel(minutes), label: waitingLabel(since, nowMs) };
}

const BASE_TITLE = 'Support Chat';

/**
 * The tab's title: rider messages that arrived while the tab was hidden
 * first ("(2) New messages · Support Chat"), else the unread count.
 */
export function supportTitle(totalUnread: number, arrivedWhileHidden: number): string {
  if (arrivedWhileHidden > 0) {
    return `(${arrivedWhileHidden}) New message${
      arrivedWhileHidden === 1 ? '' : 's'
    } · ${BASE_TITLE}`;
  }
  return totalUnread > 0 ? `(${totalUnread}) ${BASE_TITLE}` : BASE_TITLE;
}
